import { Controller } from "@hotwired/stimulus"

// P16/P17 — Deux glisser-déposer au cockpit (souris ET tactile, Pointer
// Events) :
//   - une RENCONTRE (poignée ⠿) déposée sur un TOUR → assign_round ;
//   - une PAIRE (réserve ou côté déjà rempli) déposée sur un CÔTÉ de
//     case « Déposer une paire » → assign_team.
// Les mises à jour de page arrivent par le broadcast Turbo (morph).
export default class extends Controller {
  static targets = ["zone"]
  static values = { urlTemplate: String, assignTeamUrlTemplate: String }

  connect() {
    this.onMove = this.pointerMove.bind(this)
    this.onUp = this.pointerUp.bind(this)
    // DÉLÉGATION obligatoire : ces pages vivent en morphing Turbo (chaque
    // score rafraîchit le DOM) — des écouteurs posés élément par élément
    // mourraient au premier refresh (bug vécu par Bruno, 9 sept.). Un seul
    // écouteur sur le conteneur survit à tous les morphs.
    this.onDown = (e) => {
      const handle = e.target.closest("[data-drag-handle]")
      if (handle) return this.startDrag(e, handle.closest("[data-match-id]"), "match")
      const chip = e.target.closest("[data-pair-id]")
      if (chip) this.startDrag(e, chip, "pair")
    }
    this.element.addEventListener("pointerdown", this.onDown)
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this.onDown)
    this.cleanup()
  }

  startDrag(event, element, mode) {
    if (!element) return
    event.preventDefault()
    this.mode = mode
    this.dragged = element

    this.ghost = element.cloneNode(true)
    Object.assign(this.ghost.style, {
      position: "fixed", pointerEvents: "none", opacity: "0.85", zIndex: "50",
      width: `${Math.min(element.offsetWidth, 420)}px`, left: "0", top: "0",
      background: "white", borderRadius: "0.75rem", listStyle: "none",
      boxShadow: "0 12px 30px rgba(0,0,0,0.25)", padding: "0.4rem 0.8rem"
    })
    document.body.appendChild(this.ghost)
    this.dragged.style.opacity = "0.4"
    this.moveGhost(event)

    window.addEventListener("pointermove", this.onMove)
    window.addEventListener("pointerup", this.onUp)

    // La page défile toute seule près des bords (retour Bruno) : le
    // pointeur capturé bloque le scroll naturel.
    this.scrollSpeed = 0
    this.scrollLoop = setInterval(() => {
      if (this.scrollSpeed !== 0) window.scrollBy(0, this.scrollSpeed)
    }, 16)
  }

  pointerMove(event) {
    const edge = 90
    if (event.clientY < edge) {
      this.scrollSpeed = -Math.ceil((edge - event.clientY) / 6)
    } else if (event.clientY > window.innerHeight - edge) {
      this.scrollSpeed = Math.ceil((event.clientY - (window.innerHeight - edge)) / 6)
    } else {
      this.scrollSpeed = 0
    }
    this.moveGhost(event)
    const target = this.dropTargetAt(event)
    this.highlight(target)
  }

  pointerUp(event) {
    const target = this.dropTargetAt(event)
    const dragged = this.dragged
    const mode = this.mode
    this.cleanup()
    // Une poignée dans un lien (paire d'un match terminé) : le clic qui
    // suit le drag ne doit pas naviguer.
    window.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation() },
                            { capture: true, once: true })
    if (!target || !dragged) return

    if (mode === "match") {
      const url = this.urlTemplateValue.replace(":id", dragged.dataset.matchId)
      this.patch(url, { round_id: target.dataset.zoneRoundId || null })
      const ul = target.querySelector("ul")
      if (ul) ul.appendChild(dragged)
    } else {
      const matchId = target.closest("[data-match-id]")?.dataset.matchId
      if (!matchId) return
      const url = this.assignTeamUrlTemplateValue.replace(":id", matchId)
      const body = { side: target.dataset.teamSlot, team_id: dragged.dataset.pairId }
      // Glissée depuis une autre case : l'ancien côté se libère (déplacer,
      // pas dupliquer — exemptes d'un tournoi à 12, retour Bruno)
      const fromMatch = dragged.closest("[data-match-id]")
      if (fromMatch && dragged.dataset.teamSlot && fromMatch.dataset.matchId !== matchId) {
        body.from_match_id = fromMatch.dataset.matchId
        body.from_side = dragged.dataset.teamSlot
      }
      this.patch(url, body)
      target.textContent = dragged.textContent.trim()
    }
  }

  patch(url, body) {
    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    fetch(url, {
      method: "PATCH",
      headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(body)
    }).then((r) => { if (!r.ok) window.location.reload() })
  }

  dropTargetAt(event) {
    this.ghost.style.display = "none"
    const el = document.elementFromPoint(event.clientX, event.clientY)
    this.ghost.style.display = ""
    if (!el) return null
    return this.mode === "pair"
      ? el.closest("[data-team-slot]")
      : el.closest("[data-round-dnd-target='zone']")
  }

  highlight(target) {
    this.zoneTargets.forEach((z) => {
      const on = this.mode === "match" && z === target
      z.classList.toggle("ring-2", on)
      z.classList.toggle("ring-emerald-400", on)
      z.classList.toggle("bg-emerald-500/10", on)
    })
    document.querySelectorAll("[data-team-slot]").forEach((s) => {
      const on = this.mode === "pair" && s === target
      s.classList.toggle("ring-2", on)
      s.classList.toggle("ring-emerald-400", on)
    })
  }

  moveGhost(event) {
    this.ghost.style.transform = `translate(${event.clientX - 30}px, ${event.clientY - 20}px)`
  }

  cleanup() {
    window.removeEventListener("pointermove", this.onMove)
    window.removeEventListener("pointerup", this.onUp)
    clearInterval(this.scrollLoop)
    this.scrollSpeed = 0
    this.ghost?.remove()
    if (this.dragged) this.dragged.style.opacity = ""
    this.highlight(null)
    this.ghost = null
    this.dragged = null
    this.mode = null
  }
}
